import { translationTaskConstant } from 'actionTypes';
import { TEMPORARY } from 'helpers/constant';
import {
  DOWNLOAD_TRANSLATED_FILE_STATUS,
  TASK_STATUS,
  TRANSLATED_FILE_STATUS,
  TRANSLATION_FILE_STATUS,
  VIEW_MODE,
} from 'helpers/enum';
import { Reducer } from 'redux';
import { TranslationTaskActionTypes } from 'types/actions/translation';
import {
  CurrentTranslationTask,
  TemporaryTranslationTask,
  TranslatedFile,
  TranslationTask,
} from 'types/reducers/translation';
import { v4 as uuidv4 } from 'uuid';

interface TranslationTaskState {
  tasks: {
    data: TranslationTask[];
    total_item: number;
    total_page: number;
    current_page: number;
  };
  status: TASK_STATUS;
  process_task_id: string;
  current: CurrentTranslationTask;
  temporary_translation_task: TemporaryTranslationTask;
  viewMode: VIEW_MODE;
  isLimitReached: boolean;
  downloadTranslatedFilesStatus: DOWNLOAD_TRANSLATED_FILE_STATUS;
}

const initialState: TranslationTaskState = {
  tasks: {
    data: [],
    total_item: 0,
    total_page: 0,
    current_page: 0,
  },
  status: TASK_STATUS.IDLE,
  process_task_id: '',
  current: {
    task_id: '',
    type: '',
    selectedFiles: [],
    initialSelectedFiles: [],
    isWaitingProcessFileUpload: false,
    output_languages: [],
    input_languages: [],
    results: [],
  },
  temporary_translation_task: {
    task_id: '',
    selectedFiles: [],
    output_languages: [],
    input_languages: [],
  },
  viewMode: VIEW_MODE.CHAT,
  isLimitReached: false,
  downloadTranslatedFilesStatus: DOWNLOAD_TRANSLATED_FILE_STATUS.IDLE,
};

const translationReducer: Reducer<TranslationTaskState, TranslationTaskActionTypes> = (
  state = initialState,
  action: TranslationTaskActionTypes
) => {
  switch (action.type) {
    case translationTaskConstant.UPDATE_TRANSLATION_TASKS: {
      return {
        ...state,
        tasks: action.payload,
      };
    }
    case translationTaskConstant.UPDATE_STATUS_TRANSLATION_TASK: {
      return {
        ...state,
        status: action.payload,
      };
    }
    case translationTaskConstant.UPDATE_CURRENT_TRANSLATION_TASK: {
      return {
        ...state,
        current: {
          ...state.current,
          ...action.payload,
        },
      };
    }
    case translationTaskConstant.UPDATE_PROCESS_TRANSLATION_TASK_ID: {
      return {
        ...state,
        process_task_id: action.payload,
      };
    }
    case translationTaskConstant.UPDATE_TEMPORARY_TRANSLATION_TASK_ID: {
      return {
        ...state,
        temporary_translation_task: {
          ...state.temporary_translation_task,
          task_id: action.payload,
        },
      };
    }
    case translationTaskConstant.UPDATE_CURRENT_TRANSLATED_FILES: {
      const { output_languages, selectedFiles, input_languages } = state.current;

      const translatedFiles: TranslatedFile[] = [];
      const isInititalSetupTranslatedFiles = !action.payload;
      const verifyTranslatedFiles =
        action.payload && action.payload?.length > 0 ? action.payload : state.current.results;
      const inputLanguageCode = input_languages?.[0]?.code;

      for (const outputLanguage of output_languages) {
        for (const inputFile of selectedFiles) {
          let newTranslatedFile: TranslatedFile = {
            id: uuidv4(),
            file_id: inputFile.id,
            name: `${inputLanguageCode || 'auto'}_${outputLanguage.code}_${inputFile.name}`,
            input_language_code: inputLanguageCode,
            output_language_code: outputLanguage.code,
            url: '',
            status: isInititalSetupTranslatedFiles
              ? TRANSLATED_FILE_STATUS.IDLE
              : TRANSLATED_FILE_STATUS.PENDING,
          };
          const existTranslatedFile = verifyTranslatedFiles?.find(
            (file) =>
              file.file_id === inputFile.id &&
              file.output_language_code === outputLanguage.code &&
              (file.input_language_code === inputLanguageCode ||
                (!file.input_language_code && !inputLanguageCode))
          );
          if (existTranslatedFile) {
            newTranslatedFile = {
              ...newTranslatedFile,
              ...existTranslatedFile,
            };
          }
          translatedFiles.push(newTranslatedFile);
        }
      }
      return {
        ...state,
        current: {
          ...state.current,
          results: translatedFiles,
        },
      };
    }
    case translationTaskConstant.CLEAR_CURRENT_TASK_SELECTED_FILES_TRANSLATION: {
      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: [],
        },
      };
    }
    case translationTaskConstant.CLEAR_CURRENT_TASK_SELECTED_LANGUAGES_TRANSLATION: {
      return {
        ...state,
        current: {
          ...state.current,
          output_languages: [],
          input_languages: [],
        },
      };
    }
    case translationTaskConstant.CLEAR_TEMPORARY_TRANSLATION_TASK: {
      return {
        ...state,
        temporary_translation_task: {
          task_id: '',
          data: [],
          selectedFiles: [],
          input_languages: [],
          output_languages: [],
        },
      };
    }
    case translationTaskConstant.LOAD_TEMPORARY_TRANSLATION_TASK_DATA: {
      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: state.temporary_translation_task.selectedFiles,
        },
      };
    }
    case translationTaskConstant.ADD_TRANSLATION_TASK: {
      return {
        ...state,
        tasks: {
          ...state.tasks,
          data: [action.payload, ...state.tasks.data],
          total_item: state.tasks.total_item + 1,
        },
      };
    }
    case translationTaskConstant.UPDATE_LIMIT_STATUS_TRANSLATION_TASK: {
      return {
        ...state,
        isLimitReached: action.payload,
      };
    }
    case translationTaskConstant.ADD_SELECTED_TRANSLATION_FILE: {
      // Dedup by id OR name: uploaded files get a fresh uuid on every dispatch, so an id-only
      // check never matches and the same upload was added twice. Name-dedup makes this idempotent
      // regardless of how many times the upload effect fires. (#78306)
      const isNewFileSelected = state.current.selectedFiles.some(
        (file) =>
          file.id === action.payload.id ||
          (!!file.name && !!action.payload.name && file.name === action.payload.name)
      );
      const newSelectedFiles = [
        ...state.current.selectedFiles,
        ...(!isNewFileSelected ? [action.payload] : []),
      ];

      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: newSelectedFiles,
        },
        temporary_translation_task: {
          ...state.temporary_translation_task,
          ...(state.current.type === TEMPORARY && { selectedFiles: newSelectedFiles }),
        },
      };
    }
    case translationTaskConstant.UPDATE_SELECTED_TRANSLATION_FILE: {
      if (action.payload.data.translation_file_status === TRANSLATION_FILE_STATUS.UPLOADED) {
        const isUpdateTemporaryTask = action.payload.isTemporary;
        const isCurrentTemporaryTask = state.current.type === TEMPORARY;
        let updateFileIndex;

        if (isUpdateTemporaryTask) {
          updateFileIndex = state.temporary_translation_task.selectedFiles.findIndex(
            (file) => file.translation_file_status === TRANSLATION_FILE_STATUS.IDLE
          );

          const newSelectedFiles = [...state.temporary_translation_task.selectedFiles];
          newSelectedFiles[updateFileIndex] = {
            ...newSelectedFiles[updateFileIndex],
            ...action.payload.data,
          };

          return {
            ...state,
            current: {
              ...state.current,
              ...(isCurrentTemporaryTask && {
                selectedFiles: newSelectedFiles,
              }),
            },
            temporary_translation_task: {
              ...state.temporary_translation_task,
              selectedFiles: newSelectedFiles,
            },
          };
        }

        updateFileIndex = state.current.selectedFiles.findIndex(
          (file) => file.translation_file_status === TRANSLATION_FILE_STATUS.IDLE
        );

        const newSelectedFiles = [...state.current.selectedFiles];
        newSelectedFiles[updateFileIndex] = {
          ...newSelectedFiles[updateFileIndex],
          ...action.payload.data,
        };

        return {
          ...state,
          current: {
            ...state.current,
            selectedFiles: newSelectedFiles,
          },
        };
      }
      if (action.payload.data.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED) {
        const isUpdateTemporaryTask = action.payload.isTemporary;
        const isCurrentTemporaryTask = state.current.type === TEMPORARY;
        let updateFileIndex;
        if (isUpdateTemporaryTask) {
          updateFileIndex = state.temporary_translation_task.selectedFiles.findIndex(
            (file) => file.translation_file_status === TRANSLATION_FILE_STATUS.UPLOADED
          );

          const newSelectedFiles = [...state.temporary_translation_task.selectedFiles];
          newSelectedFiles[updateFileIndex] = {
            ...newSelectedFiles[updateFileIndex],
            ...action.payload.data,
          };

          return {
            ...state,
            current: {
              ...state.current,
              ...(isCurrentTemporaryTask && {
                selectedFiles: newSelectedFiles,
              }),
            },
            temporary_translation_task: {
              ...state.temporary_translation_task,
              selectedFiles: newSelectedFiles,
            },
          };
        }

        updateFileIndex = state.current.selectedFiles.findIndex(
          (file) => file.translation_file_status === TRANSLATION_FILE_STATUS.UPLOADED
        );

        const newSelectedFiles = [...state.current.selectedFiles];
        newSelectedFiles[updateFileIndex] = {
          ...newSelectedFiles[updateFileIndex],
          ...action.payload.data,
        };

        return {
          ...state,
          current: {
            ...state.current,
            selectedFiles: newSelectedFiles,
          },
        };
      }
      return state;
    }
    case translationTaskConstant.UPDATE_TRANSLATED_FILE: {
      const translatedFiles = state.current.results ?? [];
      if (
        action.payload.data.status &&
        [TRANSLATED_FILE_STATUS.ERROR, TRANSLATED_FILE_STATUS.GENERATED].includes(
          action.payload.data.status
        )
      ) {
        const updateFileIndex = translatedFiles.findIndex(
          (file) =>
            file.file_id === action.payload.data.file_id &&
            file.output_language_code === action.payload.data.output_language_code &&
            (file.input_language_code === action.payload.data.input_language_code ||
              (!file.input_language_code && !action.payload.data.input_language_code))
        );

        const newTranslatedFiles = [...(state.current.results ?? [])];
        if (updateFileIndex > -1) {
          newTranslatedFiles[updateFileIndex] = {
            ...newTranslatedFiles[updateFileIndex],
            ...action.payload.data,
          };
        }

        return {
          ...state,
          current: {
            ...state.current,
            results: newTranslatedFiles,
          },
        };
      }
      if (action.payload.data.status === TRANSLATED_FILE_STATUS.PENDING) {
        const updateFileIndex = translatedFiles.findIndex(
          (file) =>
            file.file_id === action.payload.data.file_id &&
            file.output_language_code === action.payload.data.output_language_code &&
            file.status === TRANSLATED_FILE_STATUS.IDLE &&
            (file.input_language_code === action.payload.data.input_language_code ||
              (!file.input_language_code && !action.payload.data.input_language_code))
        );

        const newTranslatedFiles = [...(state.current.results ?? [])];
        if (updateFileIndex > -1) {
          newTranslatedFiles[updateFileIndex] = {
            ...newTranslatedFiles[updateFileIndex],
            ...action.payload.data,
          };
        }

        return {
          ...state,
          current: {
            ...state.current,
            results: newTranslatedFiles,
          },
        };
      }
      return state;
    }
    case translationTaskConstant.REMOVE_SELECTED_TRANSLATION_FILE: {
      const newSelectedFiles = state.current.selectedFiles.filter(
        (file) => file.id !== action.payload.id
      );
      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: newSelectedFiles,
        },
      };
    }
    case translationTaskConstant.UPDATE_INITIAL_SELECTED_TRANSLATION_FILES: {
      return {
        ...state,
        current: {
          ...state.current,
          initialSelectedFiles: state.current.selectedFiles,
        },
      };
    }
    case translationTaskConstant.RESET_TRANSLATION_DATA: {
      return initialState;
    }
    case translationTaskConstant.SET_INPUT_LANGUAGE: {
      return {
        ...state,
        current: {
          ...state.current,
          input_languages: action.payload,
        },
      };
    }
    case translationTaskConstant.SET_OUTPUT_LANGUAGE: {
      return {
        ...state,
        current: {
          ...state.current,
          output_languages: action.payload,
        },
      };
    }
    case translationTaskConstant.UPDATE_DOWNLOAD_TRANSLATED_FILE_STATUS: {
      return {
        ...state,
        downloadTranslatedFilesStatus: action.payload,
      };
    }
    default:
      return state;
  }
};

export default translationReducer;
